import { useDataStore } from "@/stores";
import useFilterStore from "@/stores/useFilterStore";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import clsx from "clsx";
import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import ClassificationFilter from "../Classification";
import { ClassificationFilterRef } from "../Classification/ClassificationFilter";
import DisciplineFilter from "../Discipline";
import { DisciplineFilterRef } from "../Discipline/DisciplineFilter";
import SoftwareFilter from "../Software";
import { SoftwareFilterRef } from "../Software/SoftwareFilter";
import UseCaseFilter from "../UseCase";
import { UseCaseFilterRef } from "../UseCase/UseCaseFilter";
import VersionFilter from "../Version";
import { VersionFilterRef } from "../Version/VersionFilter";

const FilterModal = () => {
    const open = useFilterStore((state) => state.open);
    const setOpen = useFilterStore((state) => state.setOpen);
    const classifications = useDataStore((state) => state.classifications);
    const useCases = useDataStore((state) => state.useCases);

    const activeVersionName = useFilterStore((state) => state.activeVersion)!;
    const unconfirmedVersionName = useFilterStore((state) => state.activeModalVersion);
    const unconfirmedDisciplines = useFilterStore((state) => state.unconfirmedDisciplines);
    const unconfirmedSoftware = useFilterStore((state) => state.unconfirmedSoftware);
    const unconfirmedUseCases = useFilterStore((state) => state.unconfirmedUseCases);
    const unconfirmedClassification = useFilterStore((state) => state.unconfirmedClassification);

    const classificationOptions = useMemo(
        () => classifications[unconfirmedVersionName || activeVersionName],
        [classifications, unconfirmedVersionName, activeVersionName]
    );

    const availableUseCases = useCases[activeVersionName];
    const invalidDisciplines = unconfirmedDisciplines.length === 0;
    const invalidSoftware = unconfirmedSoftware === null;
    const invalidUseCases =
        unconfirmedUseCases.length === 0 ||
        !unconfirmedUseCases.some((id) => {
            const useCase = availableUseCases.find((x) => x.id === id);
            if (!useCase) return false;

            if (useCase.attachedDiscipline)
                return useCase.attachedDiscipline.some((y) => unconfirmedDisciplines.includes(y));

            return true;
        });
    const invalidClassification = unconfirmedClassification === null;

    const versionFilterRef = useRef<VersionFilterRef>(null);
    const disciplineFilterRef = useRef<DisciplineFilterRef>(null);
    const ifcFilterRef = useRef<SoftwareFilterRef>(null);
    const softwareFilterRef = useRef<SoftwareFilterRef>(null);
    const useCaseFilterRef = useRef<UseCaseFilterRef>(null);
    const classificationFilterRef = useRef<ClassificationFilterRef>(null);

    const { t } = useTranslation(["filters"]);

    const handleConfirm = () => {
        versionFilterRef.current?.confirmChange();
        disciplineFilterRef.current?.confirmChange();
        ifcFilterRef.current?.confirmChange();
        softwareFilterRef.current?.confirmChange();
        useCaseFilterRef.current?.confirmChange();
        classificationFilterRef.current?.confirmChange();

        setOpen(false);
    };

    const handleReset = () => setOpen(false);

    return (
        <Dialog open={open} onClose={setOpen} className="relative z-200">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
            />

            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="container mx-auto flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel
                        transition
                        className="relative transform overflow-hidden rounded-lg flex flex-col space-y-12 bg-white px-12 m-10 text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full sm:p-6 data-closed:sm:translate-y-0 data-closed:sm:scale-95"
                    >
                        <div>
                            <DialogTitle as="span" className="sr-only">
                                {t("modal.screenreader-description", { ns: "filters" })}
                            </DialogTitle>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                                <div className="flex flex-col space-y-12">
                                    <div>
                                        <span className="grid grid-cols-[auto_auto]+ items-center">
                                            <span className="font-bold text-xl">
                                                {t("versions.title", { ns: "filters" })}
                                            </span>
                                            <span className="font-light italic text-sm text-right">
                                                {/* {t("versions.alt-text", { ns: "filters" })} */}
                                            </span>
                                        </span>
                                        <div className="grid grid-cols-2 mt-2 gap-1">
                                            <VersionFilter ref={versionFilterRef} shouldConfirm small />
                                        </div>
                                    </div>
                                    <div>
                                        <span className="grid grid-cols-[auto_auto] items-center">
                                            <span className="font-bold text-xl">
                                                {t("disciplines.title", { ns: "filters" })}
                                            </span>
                                            {invalidDisciplines && (
                                                <span
                                                    className={clsx([
                                                        "font-light italic text-sm text-right text-red-600",
                                                    ])}
                                                >
                                                    {t("disciplines.alt-text", { ns: "filters" })}
                                                </span>
                                            )}
                                        </span>
                                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 mt-2 gap-1">
                                            <DisciplineFilter ref={disciplineFilterRef} shouldConfirm small />
                                        </div>
                                    </div>
                                    <div>
                                        <span className="grid grid-cols-[auto_auto] items-center">
                                            <span className="font-bold text-xl">
                                                {t("ifc.title", { ns: "filters" })}
                                            </span>
                                            <span className="font-light italic text-sm text-right">
                                                {/* {t("ifc.alt-text", { ns: "filters" })} */}
                                            </span>
                                        </span>
                                        <div className="grid grid-cols-2 xl:grid-cols-3 mt-2 gap-1">
                                            <SoftwareFilter ifc ref={ifcFilterRef} shouldConfirm small />
                                        </div>
                                    </div>
                                    <div>
                                        <span className="grid grid-cols-[auto_auto] items-center">
                                            <span className="font-bold text-xl">
                                                {t("software.title", { ns: "filters" })}
                                            </span>
                                            {invalidSoftware && (
                                                <span
                                                    className={clsx([
                                                        "font-light italic text-sm text-right text-orange-600",
                                                    ])}
                                                >
                                                    {t("software.alt-text", { ns: "filters" })}
                                                </span>
                                            )}
                                        </span>
                                        {/* <span className="font-light text-sm mb-5 block">
                                            {t("software.description", { ns: "filters" })}
                                        </span> */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 mt-2 gap-1">
                                            <SoftwareFilter ref={softwareFilterRef} shouldConfirm small />
                                        </div>
                                    </div>
                                    {classificationOptions && classificationOptions.length > 0 && (
                                        <div>
                                            <span className="grid grid-cols-[auto_auto] items-center">
                                                <span className="font-bold text-xl">
                                                    {t("classifications.title", { ns: "filters" })}
                                                </span>
                                                {invalidClassification && (
                                                    <span
                                                        className={clsx([
                                                            "font-light italic text-sm text-right text-orange-600",
                                                        ])}
                                                    >
                                                        {t("classifications.alt-text", { ns: "filters" })}
                                                    </span>
                                                )}
                                            </span>
                                            <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 mt-2 gap-1">
                                                <ClassificationFilter
                                                    ref={classificationFilterRef}
                                                    shouldConfirm
                                                    small
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <span className="grid grid-cols-[auto_auto] items-center">
                                        <span className="font-bold text-xl">
                                            {t("usecases.title", { ns: "filters" })}
                                        </span>
                                        {invalidUseCases && (
                                            <span
                                                className={clsx(["font-light italic text-sm text-right text-red-600"])}
                                            >
                                                {t("usecases.alt-text", { ns: "filters" })}
                                            </span>
                                        )}
                                    </span>
                                    <div className="grid grid-cols-1 2xl:grid-cols-2 mt-2 gap-1">
                                        <UseCaseFilter ref={useCaseFilterRef} shouldConfirm small />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-flow-row-dense grid-cols-2 sm:gap-3 mx-auto">
                            <button
                                type="button"
                                onClick={handleConfirm}
                                className={clsx([
                                    "inline-flex w-full justify-center rounded-md bg-primary-500 px-5 py-3 text-white shadow-xs hover:bg-primary-600 sm:col-start-2 cursor-pointer",
                                    "disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed disabled:hover:bg-gray-200 disabled:hover:text-gray-500",
                                ])}
                                disabled={invalidUseCases || invalidDisciplines}
                            >
                                {t("modal.apply-button", { ns: "filters" })}
                            </button>
                            <button
                                type="button"
                                data-autofocus
                                onClick={handleReset}
                                className="mt-3 inline-flex w-full justify-center rounded-md bg-gray-100 px-5 py-3 text-gray-900 shadow-xs hover:bg-gray-50 sm:col-start-1 sm:mt-0 cursor-pointer"
                            >
                                {t("modal.reset-button", { ns: "filters" })}
                            </button>
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
};

export default FilterModal;
